import { MAX_CONSECUTIVE_ERRORS } from './constants';
import { presentedValue } from './exercises/truefalse';
import type { FactId } from './facts';
import { mulberry32, shuffle } from './rng';
import type { Task } from './session';

export type Phase = 'awaiting' | 'feedbackCorrect' | 'feedbackWrong' | 'guided' | 'done';

export interface QueueState {
  queue: Task[];
  taskCount: number;
  /** Заданий убрано из очереди: верных плюс закрытых с помощью. */
  completed: number;
  assisted: number;
  consecutiveErrors: Record<string, number>;
  showIndex: Record<string, number>;
  /** Факты текущего задания, раскрытые последним неверным ответом; пусто
   *  после верного (раздел 5.7). Интерфейс читает это поле, чтобы
   *  записать строки в журнал раскрытий. */
  lastRevealed: FactId[];
  phase: Phase;
}

/** Факты, которые раскрывает задание при неверном ответе: для сравнения —
 *  оба выражения, для остальных типов — единственный факт (раздел 5.7). */
function revealedFactIds(task: Task): FactId[] {
  return task.type === 'compare' && task.partner
    ? [task.fact.id, task.partner.id]
    : [task.fact.id];
}

/** FNV-1a, 32 бита. Не для стойкости — только чтобы превратить строку
 *  `taskId:showIndex` в затравку для `mulberry32`, детерминированно и без
 *  внешнего генератора (раздел 5.4). */
function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Пересборка задания при возврате в очередь после неверного ответа.
 *  Перестановка/значение детерминированы парой (`taskId`, `nextShowIndex`) —
 *  никакого внешнего генератора здесь нет и не нужно: раздел 5.4 спеки
 *  требует именно этого, а не воспроизводимости от состояния генератора
 *  сессии. Для `choice4` значения не меняются, меняется только порядок
 *  кнопок (раздел 5.4). Для `truefalse` предъявленное значение
 *  генерируется заново (раздел 5.5) — иначе второй показ решается без
 *  знания таблицы. Для `compare` задание не меняется. */
function reshowTask(task: Task, nextShowIndex: number): Task {
  const rng = mulberry32(hashString(`${task.id}:${nextShowIndex}`));

  if (task.type === 'choice4' && task.options) {
    return { ...task, options: shuffle(task.options, rng) };
  }
  if (task.type === 'truefalse') {
    return { ...task, presented: presentedValue(task.fact, rng() < 0.5, rng) };
  }
  return task;
}

export function initQueue(tasks: readonly Task[]): QueueState {
  if (tasks.length === 0) throw new Error('initQueue: пустая сессия недопустима');
  /** Идентификаторы заданий обязаны быть различны, и нарушение — крик, а не
   *  тихое продолжение.
   *
   *  По ним ключуются счётчик подряд идущих ошибок и номер показа. При
   *  дубле третья ошибка ОДНОГО задания закрывает подсказкой ДРУГОЕ:
   *  ребёнок видит верный ответ на пример, которого не решал, а факт
   *  получает раскрытие и перестаёт считаться освоенным на двенадцать
   *  часов. На экране это выглядит как случайность, и найти причину по
   *  журналу нельзя — оба задания в нём настоящие.
   *
   *  Сегодня идентификаторы производит движок и они различны. Проверка
   *  стоит здесь потому, что задания приходят и из повтора ошибок
   *  (`buildRepeatSession`), а завтра могут прийти из журнала. */
  const seen = new Set<string>();
  for (const task of tasks) {
    if (seen.has(task.id)) {
      throw new Error(`initQueue: идентификатор задания повторяется: ${task.id}`);
    }
    seen.add(task.id);
  }
  return {
    queue: [...tasks],
    taskCount: tasks.length,
    completed: 0,
    assisted: 0,
    consecutiveErrors: {},
    showIndex: Object.fromEntries(tasks.map((t) => [t.id, 1])),
    lastRevealed: [],
    phase: 'awaiting',
  };
}

export function currentTask(state: QueueState): Task | null {
  return state.queue[0] ?? null;
}

export function progress(state: QueueState): number {
  return state.completed / state.taskCount;
}

export function submit(state: QueueState, isCorrect: boolean): QueueState {
  if (state.phase !== 'awaiting') {
    throw new Error(
      `submit: ответ принимается только в фазе ожидания, текущая фаза ${state.phase}`,
    );
  }

  const task = currentTask(state);
  if (!task) throw new Error('submit: очередь пуста');

  if (isCorrect) {
    return {
      ...state,
      consecutiveErrors: { ...state.consecutiveErrors, [task.id]: 0 },
      lastRevealed: [],
      phase: 'feedbackCorrect',
    };
  }

  const errors = (state.consecutiveErrors[task.id] ?? 0) + 1;
  return {
    ...state,
    consecutiveErrors: { ...state.consecutiveErrors, [task.id]: errors },
    /** Верный ответ раскрывается после каждой ошибки, включая ту, что
     *  закрывает задание с помощью (раздел 5.7). */
    lastRevealed: revealedFactIds(task),
    /** Выхода по перебору не существует: ничто не мешает выбирать одно и то
     *  же неверное значение бесконечно (решение (m)). */
    phase: errors >= MAX_CONSECUTIVE_ERRORS ? 'guided' : 'feedbackWrong',
  };
}

export function advance(state: QueueState): QueueState {
  const task = currentTask(state);
  /** `lastRevealed` обнуляется при каждом переходе.
   *
   *  Поле описывает ОДИН момент — то, что раскрылось прямо сейчас, — и
   *  держать его дольше значит хранить ложь: в фазе ожидания раскрывать
   *  было нечего. Потребитель сегодня один и читает поле сразу после
   *  `submit`, то есть до перехода; но контракт «читать сразу» нигде не
   *  записан, а цена ошибки высокая. Лишняя строка раскрытия в журнале
   *  снимает освоенность факта на двенадцать часов, и понять по журналу,
   *  что раскрытия не было, нельзя: строка выглядит настоящей.
   *
   *  Дешевле сделать состояние честным в каждой фазе, чем требовать от
   *  потребителей помнить про порядок чтения. */
  if (!task) return { ...state, lastRevealed: [], phase: 'done' };

  if (state.phase === 'feedbackCorrect' || state.phase === 'guided') {
    const queue = state.queue.slice(1);
    return {
      ...state,
      queue,
      completed: state.completed + 1,
      assisted: state.phase === 'guided' ? state.assisted + 1 : state.assisted,
      lastRevealed: [],
      phase: queue.length === 0 ? 'done' : 'awaiting',
    };
  }

  if (state.phase === 'feedbackWrong') {
    const nextShowIndex = (state.showIndex[task.id] ?? 1) + 1;
    const queue = [...state.queue.slice(1), reshowTask(task, nextShowIndex)];
    return {
      ...state,
      queue,
      showIndex: { ...state.showIndex, [task.id]: nextShowIndex },
      lastRevealed: [],
      phase: 'awaiting',
    };
  }

  return state;
}
